import React, { useCallback, useEffect } from "react";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InputAdornment, TextField } from "@mui/material";
import { parse, ParsedQs } from "qs";
import { useNavigate } from "react-router-dom";
import { changeQuery } from "../utils/navigate";
import { MessageDescriptor, useIntl } from "react-intl";

type StringFilterProps = {
    filterField: string; /// name of the filter as set in the URI
    placeholder?: MessageDescriptor;
    searchDelay?: number; /// delay in milliseconds before applying the filter after a change
};

/**
 * Provides an input for filter that filters by a given name.
 */
const StringFilter: React.FC<StringFilterProps> = ({ filterField, placeholder, searchDelay = 300 }) => {
    const navigate = useNavigate();
    const {formatMessage} = useIntl();

    // Initialize value based on the URL using qs parsing
    const initialValue = (() => {
        const searchParams = parse(window.location.search, { ignoreQueryPrefix: true });
        if (searchParams.filters && typeof searchParams.filters === "object") {
            const filtersP = searchParams.filters as ParsedQs;

            if (filtersP.column === filterField) {
                const filterValue = filtersP.value;
                if (typeof filterValue === "string") {
                    return filterValue.replace(/%/g, ""); // Remove the % characters
                }
            }
        }
        return "";
    })();

    const [value, setValue] = React.useState(initialValue);
    const timerRef = React.useRef<NodeJS.Timeout | null>(null);
    const isInitialMount = React.useRef(true);

    const buildFilterQuery = useCallback(() => {
        if (!value) return {};
        return {
            column: filterField,
            value: `%${value}%`,
            operator: "ilike",
        };
    }, [value, filterField]);

    const applyFilter = useCallback(() => {
        changeQuery(navigate, ["page", "1"], ["filters", buildFilterQuery()]);
    }, [navigate, buildFilterQuery]);

    useEffect(() => {
        // do not apply on the initial render
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        // Clear previous timer if it exists
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        if (value == "") applyFilter();
        else {
            // Set a new timer
            timerRef.current = setTimeout(() => {
                applyFilter();
            }, searchDelay);

            // Cleanup on unmount or when value changes
            return () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                }
            };
        }
    }, [value, searchDelay, applyFilter]);

    return (
        <TextField
            type="text"
            name={filterField}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={placeholder ? formatMessage(placeholder) : undefined}
            size="small"
            slotProps={{
                input: {
                    endAdornment: (
                        <InputAdornment position="end">
                            <FontAwesomeIcon icon={faTimes} onClick={() => setValue("")} />
                        </InputAdornment>
                    ),
                },
            }}
        />
    );
};

export default StringFilter;
